import { useAuthStore } from '../store/auth.store';
import { apiFetch } from './api';
import { useUiStore } from '../store/ui.store';

const ALLOWED_ORIGINS = (import.meta.env.VITE_ALLOWED_ORIGINS ?? '*').split(
  ',',
);

let refreshTimer: ReturnType<typeof setTimeout> | null = null;

function scheduleRefresh(expiresAt: string) {
  if (refreshTimer) clearTimeout(refreshTimer);
  const msUntilRefresh =
    new Date(expiresAt).getTime() - Date.now() - 5 * 60 * 1000;
  if (msUntilRefresh <= 0) return;
  refreshTimer = setTimeout(() => {
    window.parent.postMessage({ type: 'REQUEST_AUTH_TOKEN' }, '*');
  }, msUntilRefresh);
}

export function initHostBridge() {
  window.addEventListener('message', async (event) => {
    if (ALLOWED_ORIGINS[0] !== '*' && !ALLOWED_ORIGINS.includes(event.origin))
      return;

    if (event.data?.type === 'AUTH_TOKEN') {
      await exchangeToken(event.data.token);
    }

    if (event.data?.type === 'RECORD_CONTEXT' && event.data.record) {
      apiFetch('/api/chat/host-events', {
        method: 'POST',
        body: JSON.stringify({
          type: 'record_context',
          record: event.data.record,
        }),
      }).catch(console.error);
    }

    if (event.data?.type === 'THEME_CHANGE') {
      const root = document.documentElement;

      // Apply design token overrides
      if (event.data.tokens && typeof event.data.tokens === 'object') {
        for (const [key, value] of Object.entries(
          event.data.tokens as Record<string, string>,
        )) {
          // Only allow CSS custom properties (must start with --)
          if (key.startsWith('--')) {
            root.style.setProperty(key, value);
          }
        }
      }

      // Still support dark/light class toggle
      if (event.data.theme === 'dark' || event.data.theme === 'light') {
        root.classList.toggle('dark', event.data.theme === 'dark');
      }
    }

    if (event.data?.type === 'NAVIGATE' && event.data.surface) {
      useUiStore.getState().setSurface(event.data.surface);
    }
  });

  // Ready signal: our listener is up, so ask the host for a token now.
  // The host replies with AUTH_TOKEN (pinned origin) — no load-race.
  window.parent.postMessage({ type: 'REQUEST_AUTH_TOKEN' }, '*');
}

export async function exchangeToken(hostToken: string) {
  const res = await fetch('/api/chat/auth/token-exchange', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token: hostToken }),
  });
  if (!res.ok) throw new Error('Token exchange failed');
  const { accessToken, expiresAt, role } = await res.json();
  console.log('role', role);
  useAuthStore.getState().setTokens(accessToken, expiresAt, role ?? 'user');
  scheduleRefresh(expiresAt);

  // Handshake is done and the token is in the store — now pull the current
  // record from the host. Requesting only after auth avoids the load race where
  // an eager RECORD_CONTEXT push would fire before the token exists (401).
  requestRecordContext();
}

export function openRecord(recordId: string, recordType: string) {
  window.parent.postMessage({ type: 'OPEN_RECORD', recordId, recordType }, '*');
}

export function openSearch(query?: string) {
  window.parent.postMessage({ type: 'OPEN_SEARCH', query }, '*');
}

export function openBilling() {
  window.parent.postMessage({ type: 'OPEN_BILLING' }, '*');
}

export function requestRecordContext() {
  console.log('[chat] posting REQUEST_RECORD_CONTEXT to parent');
  window.parent.postMessage({ type: 'REQUEST_RECORD_CONTEXT' }, '*');
}

export function resize(height: number) {
  window.parent.postMessage({ type: 'RESIZE', height }, '*');
}
